import request from './request';
import { logout } from '../store/slices/adminSlices';

/**
 * Backend must expose: POST {API_BASE}/auth/logout (clears session cookie, returns 204).
 * Admin API uses REACT_APP_PRODUCTION_BASE_URL / REACT_APP_DEVELOPMENT_BASE_URL (see request.js).
 */
const ADMIN_ORIGIN = process.env.REACT_APP_ADMIN_ORIGIN || 'https://admin.bellhopt.com';

/**
 * Full URL to send the user to after logout (admin login page).
 * Uses REACT_APP_ADMIN_ORIGIN when set, otherwise https://admin.bellhopt.com.
 */
export function getLogoutRedirectUrl() {
  const base = ADMIN_ORIGIN.replace(/\/$/, '');
  return `${base}/login`;
}

/**
 * Clears client state immediately and tells backend to clear cookie.
 * Caller should redirect to login (e.g. via performLogoutAndRedirect) — do not await.
 * If we awaited the backend request, a hang/failure would block UI and "nothing happened";
 * clearing first guarantees logout always completes locally.
 * @param {Function} dispatch - Redux dispatch
 */
export function performLogout(dispatch) {
  dispatch(logout());
  // Fire logout so backend clears cookie; don't block UI. Brief delay before
  // redirect in callers gives this a chance to reach the server.
  request.post('/auth/logout', {}, { withCredentials: true, timeout: 5000 }).catch(() => {});
}

/**
 * Performs logout then redirects to admin.bellhopt.com/login (or REACT_APP_ADMIN_ORIGIN/login).
 * Waits for the backend to clear the session cookie (or 1.5s timeout) before redirecting
 * so the user is actually logged out and doesn't reappear logged in after the redirect.
 * @param {Function} dispatch - Redux dispatch
 */
export function performLogoutAndRedirect(dispatch) {
  dispatch(logout());
  const logoutRequest = request.post('/auth/logout', {}, { withCredentials: true, timeout: 5000 }).catch(() => {});

  const redirectAfterLogout = () => {
    window.location.href = getLogoutRedirectUrl();
  };

  // Wait for backend to clear cookie, then redirect. Cap at 1.5s so we don't hang.
  Promise.race([
    logoutRequest,
    new Promise((r) => setTimeout(r, 1500)),
  ]).then(redirectAfterLogout);
}
